import { Box, Typography } from '@mui/material';
import { createElement, useMemo } from 'react';
import type { ReactNode } from 'react';

import { useT } from '../i18n/LanguageContext';

import { DROPPED_HTML_TAGS, PRODUCT_HTML_TAGS, safeHref } from './productHtml';

type HtmlPreviewProps = {
  html: string;
  /** What to say when there is nothing to show. */
  emptyText?: string;
};

/** React elements for the allowed tags only — never `innerHTML`, so nothing in the text can run. */
const render = (node: Node, key: string): ReactNode => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const element = node as Element;
  const tag = element.tagName.toLowerCase();

  if (DROPPED_HTML_TAGS.has(tag)) {
    return null;
  }

  const children = Array.from(element.childNodes).map((child, index) => render(child, `${key}.${index}`));

  if (!PRODUCT_HTML_TAGS.has(tag)) {
    return children;
  }

  if (tag === 'br') {
    return createElement('br', { key });
  }

  if (tag === 'a') {
    const href = safeHref(element.getAttribute('href'));

    return createElement(
      'a',
      href ? { key, href, target: '_blank', rel: 'noopener noreferrer' } : { key },
      children,
    );
  }

  return createElement(tag, { key }, children);
};

/**
 * A product description as a shop would show it (R0030 WP-05), from HTML that may still hold anything — it is
 * parsed into an inert document and only the allowed tags become elements. The API sanitises what it stores.
 */
export const HtmlPreview = ({ html, emptyText }: HtmlPreviewProps) => {
  const t = useT();
  const nodes = useMemo(() => {
    const body = new DOMParser().parseFromString(html, 'text/html').body;

    return Array.from(body.childNodes).map((child, index) => render(child, String(index)));
  }, [html]);

  if (html.trim() === '') {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyText ?? t('products.previewEmpty')}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        typography: 'body2',
        '& p': { my: 0.75 },
        '& h2, & h3, & h4': { mt: 1.5, mb: 0.5 },
        '& ul, & ol': { my: 0.75, pl: 3 },
        '& blockquote': { borderLeft: 3, borderColor: 'divider', pl: 1.5, mx: 0, color: 'text.secondary' },
        '& a': { color: 'primary.main' },
      }}
    >
      {nodes}
    </Box>
  );
};
